import { Box, Divider, makeStyles } from '@material-ui/core';
import { CheckCircleOutlineSharp } from '@material-ui/icons';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { ChevronDown, ChevronUp, Edit2 } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../../theme/theme';
import { Badge, Button, HighlightCard, IconButton, motions } from '../../../global';

const mod = 'cards.SectionCard';

const useSectionCardClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        transition: `border-top ${theme.transitions.default}s`,
        '&.unfocused': {
            borderTop: `0px solid transparent`,
        },
    },
    header: {
        display: 'flex',
        flex: 1,
        padding: 20,
        borderRadius: 5,
        overflow: 'hidden',
        background: theme.colors.neutral[4],
    },
    divider: {
        background: theme.colors.neutral[6],
    },
    children: {
        display: 'flex',
        padding: 20,
    },
});

/**
 * Section card component.
 * The section card children cannot be a component that is connected the the redux store.
 * The animation will fail because of issues with the redux connect wrapper.
 *
 * @param props.focused highlight the card header content
 * @param props.display display the card children content
 * @param props.headerKey header key identifier, if changed, the transition effect is applied to the header.
 *        The transition is also always applied when `props.focused` changes
 * @param props.childrenKey children key identifier, if changed, the transition effect is applied to the children.
 *        The transition is also always applied when `props.focused` or `props.display` change
 * @param props.header header content
 * @param props.children children content
 *
 */
export const SectionCard = (props: {
    focused: boolean;
    display: boolean;
    headerKey?: React.Key;
    childrenKey?: React.Key;
    header: React.ReactNode;
    children: React.ReactNode;
}) => {
    const classes = useSectionCardClasses();
    const status = { focused: props.focused, unfocused: !props.focused };

    return (
        <HighlightCard className={clsx(classes.root, status)} layout>
            <AnimatePresence initial={false} exitBeforeEnter>
                <motion.div
                    key={props.headerKey}
                    className={classes.header}
                    layout
                    {...motions.presence({ opacity: 0.5 })}
                >
                    {props.header}
                </motion.div>
            </AnimatePresence>
            {props.focused && props.display && (
                <motion.div layout>
                    <Divider className={classes.divider} />
                </motion.div>
            )}
            <AnimatePresence initial={false} exitBeforeEnter>
                {props.display && (
                    <motion.div
                        key={props.childrenKey}
                        className={classes.children}
                        layout
                        {...motions.presence({ opacity: 0.5 })}
                    >
                        {props.children}
                    </motion.div>
                )}
            </AnimatePresence>
        </HighlightCard>
    );
};

const useSectionCardTitleClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
        '&.focused': {
            maxWidth: 500,
        },
        color: theme.colors.darkBlue[5],
    },
    message: {
        ...theme.typography.content.caption.paragraph,
    },
    title: {
        '&.focused': {
            ...theme.typography.headings.h1,
            marginBottom: 5,
        },
        '&.unfocused': {
            ...theme.typography.headings.h5,
            marginBottom: 3,
        },
    },
    subtitle: {
        ...theme.typography.content.caption.default,
        '&.unfocused': {
            opacity: 0.5,
        },
    },
});

/**
 * Section card default header description containing title and subtitle.
 * The typography changes if the section card is focused or not.
 *
 * @param props.focused section card focused state
 * @param props.message header small message on top of the title
 * @param props.title header title
 * @param props.subtitle header subtitle
 */
export const SectionCardTitle = (props: { focused: boolean; message?: string; title: string; subtitle?: string }) => {
    const classes = useSectionCardTitleClasses();
    const status = { focused: props.focused, unfocused: !props.focused };

    return (
        <div className={clsx(classes.root, status)}>
            {props.focused && props.message != undefined && (
                <span className={clsx(classes.message, status)}>{props.message}</span>
            )}
            <span className={clsx(classes.title, status)}>{props.title}</span>
            <span className={clsx(classes.subtitle, status)}>{props.subtitle}</span>
        </div>
    );
};

const useSectionCardMenuClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
    },
    button: {
        margin: '-10px 10px',
    },
    toggle: {
        margin: -10,
        color: theme.colors.neutral[1],
    },
    badge: {
        margin: '0 10px',
    },
});

/**
 * Utility menu for section cards, it contains display toggle actions, a badge display, and edit buttons.
 * This component does not trigger any action to a parent section header unless explicitly implemented through the
 * callbacks.
 *
 * @param props.display section card display state
 * @param props.editing editing status of the children components in the section header (conceptual)
 * @param props.verified status of the badge
 * @param props.onDisplay callback called when `display` icon is toggled by some action in the header menu
 * @param props.onEdit callback called when `edit` button is toggled by some action in the header menu
 * @param props.onVerify callback called when `verify` button is toggled by some action in the header menu
 * @param props.showBadge show the pending and verified badges
 * @param props.showVerify if the verify option is available
 */
export const SectionCardMenu = (props: {
    display: boolean;
    editable: boolean;
    verified: boolean;
    onDisplay?: (display: boolean) => void;
    onEditable?: (edit: boolean) => void;
    onVerified?: (verified: boolean) => void;
    showBadge?: boolean;
    showVerify?: boolean;
}) => {
    const classes = useSectionCardMenuClasses();
    const { t } = useTranslation(['inspection-creation']);

    return (
        <Box className={classes.root}>
            {props.display && (
                <>
                    {!props.editable && (
                        <Button
                            className={classes.button}
                            size='large'
                            endIcon={<Edit2 />}
                            onClick={() => (props.onEditable?.(true), props.onVerified?.(false))}
                        >
                            {t(`${mod}.edit`)}
                        </Button>
                    )}
                    {props.showVerify && !props.verified && (
                        <Button
                            className={classes.button}
                            size='large'
                            endIcon={<CheckCircleOutlineSharp />}
                            onClick={() => (props.onVerified?.(true), props.onDisplay?.(false))}
                        >
                            {t(`${mod}.verify`)}
                        </Button>
                    )}
                </>
            )}
            {props.showBadge && (
                <Badge className={classes.badge} type={props.verified ? 'success' : 'warning'}>
                    {t(`${mod}.${props.verified ? 'verified' : 'pending'}`)}
                </Badge>
            )}
            <IconButton
                className={classes.toggle}
                size='medium'
                onClick={() => (props.onEditable?.(false), props.onDisplay?.(!props.display))}
            >
                {props.display ? <ChevronUp /> : <ChevronDown />}
            </IconButton>
        </Box>
    );
};
